import { EnterpriseProfileType } from "@mg/schemas/src/commons";
import { CheckCircle, PaperPlaneRight } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUI } from "../contexts/ui";
import { errorAnalyticsPayload, useAnalytics } from "../utils/analytics";
import { useAppSelector } from "../utils/hooks";
import { useInviteUser } from "../utils/queries/users";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Split free-form input into a de-duplicated list of email tokens. Accepts a
 * single email or many separated by commas, semicolons, or whitespace/newlines
 * so users can paste a list however they have it. De-dup is case-insensitive
 * but preserves the first-seen spelling.
 */
export function parseEmailList(raw: string): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const token of raw.split(/[\s,;]+/)) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

/**
 * Lightweight, email-only invite modal surfaced from the sidebar for any
 * non-Restricted user. Unlike the Manage Users invite form there is no name or
 * role input: invited people are always created as Normal (OPS) by the christo
 * invite handler, and they fill in their own name during signup. Accepts a
 * single email or a comma/newline-separated list for bulk invites.
 */
export function InvitePeopleDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { notify } = useUI();
  const posthog = useAnalytics("InvitePeopleDialog");
  const user = useAppSelector((state) => state.auth.value);
  const inviteUserMutation = useInviteUser();
  const [value, setValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation("ui");

  useEffect(() => {
    if (!open) {
      setValue("");
      setIsSubmitting(false);
    }
  }, [open]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const startTime = performance.now();

    const emails = parseEmailList(value);
    const valid = emails.filter((email) => EMAIL_RE.test(email));
    const invalid = emails.filter((email) => !EMAIL_RE.test(email));

    if (!valid.length) {
      notify({
        variant: "error",
        title: invalid.length ? "No valid email addresses" : "Enter an email",
        message: invalid.length
          ? `These don't look like valid emails:\n${invalid.join("\n")}`
          : "Please enter at least one email address.",
      });
      return;
    }

    setIsSubmitting(true);

    const succeeded: string[] = [];
    const failed: { email: string; message: string; error: unknown }[] = [];

    // Send one at a time so we don't hammer the invite endpoint with a burst.
    for (const email of valid) {
      try {
        await inviteUserMutation.mutateAsync({
          enterpriseId: user?.enterpriseId as string,
          name: "",
          email,
          role: EnterpriseProfileType.OPS,
        });
        succeeded.push(email);
      } catch (error) {
        failed.push({
          email,
          message: error instanceof Error ? error.message : String(error),
          error,
        });
      }
    }

    setIsSubmitting(false);

    if (failed.length) {
      notify({
        variant: "error",
        title: `Failed to invite ${failed.length.toLocaleString()} ${
          failed.length === 1 ? "person" : "people"
        }`,
        message: failed.map((f) => `${f.email}: ${f.message}`).join("\n"),
      });
    }
    if (invalid.length) {
      notify({
        variant: "error",
        title: `${invalid.length.toLocaleString()} invalid email${
          invalid.length === 1 ? "" : "s"
        }`,
        message: invalid.join("\n"),
      });
    }
    if (succeeded.length) {
      notify({
        variant: "success",
        title:
          succeeded.length === 1
            ? `Invitation sent to ${succeeded[0]}`
            : `${succeeded.length.toLocaleString()} invitations sent`,
        message: "They'll receive an email to join your enterprise.",
        leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
      });
    }

    const durationSeconds = (performance.now() - startTime) / 1000;
    if (emails.length === 1) {
      // Single-email submit keeps the original per-invite analytics shape.
      if (succeeded.length === 1) {
        posthog.capture("user_invited", {
          userEmail: succeeded[0],
          userRole: EnterpriseProfileType.OPS,
          source: "sidebar",
          durationSeconds,
        });
      } else if (failed.length === 1) {
        posthog.capture("user_invited_error", {
          userEmail: failed[0].email,
          userRole: EnterpriseProfileType.OPS,
          source: "sidebar",
          durationSeconds,
          ...errorAnalyticsPayload(failed[0].error),
        });
      }
    } else {
      posthog.capture("bulk_invite_users", {
        source: "sidebar",
        userRole: EnterpriseProfileType.OPS,
        numRequested: emails.length,
        numSucceeded: succeeded.length,
        numFailed: failed.length,
        numInvalid: invalid.length,
        succeeded,
        durationSeconds,
      });
    }

    if (succeeded.length) {
      setValue("");
      onOpenChange(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{ maxWidth: 440 }}
        data-testid="invite-people-dialog"
        aria-labelledby="invite-people-title"
      >
        <Dialog.Title id="invite-people-title">
          {t("invitePeople")}
        </Dialog.Title>
        <Dialog.Description mb="4">
          {t("enterEmailAddresses")}
        </Dialog.Description>
        <form
          onSubmit={handleSubmit}
          data-testid="invite-people-form"
          aria-label={t("invitePeopleLower")}
        >
          <Flex direction="column" gap="3">
            <label htmlFor="invite-people-email-input">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("emails")}
              </Text>
              <TextArea
                id="invite-people-email-input"
                placeholder={t(
                  "invitePeopleDialog.nameExampleComTeammateExample",
                )}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid="invite-people-email"
                aria-label={t("emailAddressesToInvite")}
                rows={3}
                required
              />
            </label>
          </Flex>
          <Flex gap="2" mt="4" justify="end">
            <Dialog.Close data-testid="invite-people-cancel">
              <Button variant="soft" color="gray">
                {t("cancel")}
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              data-testid="invite-people-confirm"
              disabled={isSubmitting || value.trim().length === 0}
              loading={isSubmitting}
            >
              <PaperPlaneRight />
              Send{" "}
              {parseEmailList(value).length > 1
                ? t("invitePeopleDialog.invitations")
                : t("invitePeopleDialog.invitation")}
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
